import { Injectable } from '@angular/core';
import { IgniteHelperService } from '.';
import { MortgageType, ProductType } from 'apps/shared/src/models';

interface LtvInputs {
  mortgageType?: MortgageType;
  loanAmount?: number | null;
  propertyValue?: number | null;
  outstandingBalanceCurrentMortgage?: number | null;
}

interface LtvDiscountedPriceInputs {
  loanAmount?: number | null;
  discountedPurchasePrice?: number | null;
}

interface LtsInputs {
  productTypeExtended?: ProductType;
  loanAmount?: number | null;
  purchasePriceShare?: number | null;
}

interface SharePercentInputs {
  productTypeExtended?: ProductType;
  propertyValue?: number | null;
  purchasePriceShare?: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class LtvService {
  public constructor(
    private helperService: IgniteHelperService,
  ) { }

  public getPurchaseLTVFieldLabel(productType?: ProductType):string {
    if (this.helperService.isSharedOwnership(productType)) {
      return 'Share %';
    } else if (productType === ProductType.RightToBuy) {
      return 'LTV (Full market value)';
    }
    return 'LTV';
  }


  public calculateLtv({ mortgageType, loanAmount, propertyValue, outstandingBalanceCurrentMortgage }: LtvInputs) {
    if (mortgageType === MortgageType.SecondCharge) {
      return loanAmount && propertyValue && outstandingBalanceCurrentMortgage
        ? Math.ceil(100 * (loanAmount + outstandingBalanceCurrentMortgage) / propertyValue)
        : null;
    }

    return loanAmount && propertyValue
      ? Math.ceil(100 * loanAmount / propertyValue)
      : null;
  }

  public calculateLtvDiscountedPrice({ loanAmount, discountedPurchasePrice }: LtvDiscountedPriceInputs) {
    return loanAmount && discountedPurchasePrice
      ? Math.ceil(100 * loanAmount / discountedPurchasePrice)
      : null;
  }

  public calculateLts(
    {
      productTypeExtended,
      loanAmount,
      purchasePriceShare,
    } : LtsInputs,
  ) {
    if (productTypeExtended !== ProductType.SharedOwnership) {
      return null;
    }

    if (loanAmount && purchasePriceShare) {
      return Math.ceil((loanAmount / purchasePriceShare) * 100);
    }

    return null;
  }

  public calculateLtvString(inputs: LtvInputs, addPercentSymbol = true): string {
    return this.ltvToString(this.calculateLtv(inputs), addPercentSymbol);
  }

  private ltvToString(ltv: unknown, addPercentSymbol = true) {
    if (!ltv) {
      return '';
    }

    return typeof ltv === 'number' && ltv > 0
      ? `${ltv}${addPercentSymbol ? '%' : ''}`
      : '';
  }

  public calculateLtsString(inputs: LtsInputs, addPercentSymbol = true): string {
    return this.ltvToString(this.calculateLts(inputs), addPercentSymbol);
  }

  public calculateLtvDiscountedPriceString(inputs: LtvDiscountedPriceInputs, addPercentSymbol = true): string {
    return this.ltvToString(this.calculateLtvDiscountedPrice(inputs), addPercentSymbol);
  }

  public calculateSharePercent({ productTypeExtended, propertyValue, purchasePriceShare }: SharePercentInputs) {
    if (productTypeExtended === ProductType.SharedOwnership && propertyValue && purchasePriceShare) {
      return Math.ceil(100 * purchasePriceShare / propertyValue);
    }

    return null;
  }

  public calculateSharePercentString(inputs: SharePercentInputs, addPercentSymbol = true): string {
    return this.ltvToString(this.calculateSharePercent(inputs), addPercentSymbol);
  }


  public isLtvValid(inputs: LtvInputs): boolean {
    const ltv = this.calculateLtv(inputs);
    return ltv === null || ltv <= 500;
  }

  public isLtsValid(inputs: LtsInputs): boolean {
    const lts = this.calculateLts(inputs);
    return !!lts && lts <= 500;
  }
}
